import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Download, Loader2, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Avatar } from '@/components/ui/Avatar';
import { Takeover } from '@/components/ui/Takeover';
import { toast } from '@/components/ui/Toaster';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth.store';
import { formsApi, type Form, type FormField, type FormResponse } from '@/lib/api';
import { FormRenderer } from './FormRenderer';
import { ResponseStatusPill } from './FormListRow';
import { formatAnswer, formatDateShort } from './form-utils';

interface ResponsesPanelProps {
  form: Form;
  fields: FormField[];
  responses: FormResponse[];
  onChanged: () => void;
  openResponseId: string | null;
  onOpenResponse: (responseId: string | null) => void;
}

/** Submitted answers as a table — one row per response, one column per question — and a detail view. */
export function ResponsesPanel({ form, fields, responses, onChanged, openResponseId, onOpenResponse }: ResponsesPanelProps) {
  const [exporting, setExporting] = useState(false);
  const submitted = useMemo(() => responses.filter((r) => r.status === 'submitted').sort((a, b) => (b.submittedAt || '').localeCompare(a.submittedAt || '')), [responses]);
  const columns = useMemo(() => fields.filter((f) => f.type !== 'section'), [fields]);
  // Show the first handful of questions inline; the rest live in the detail view.
  const inline = columns.slice(0, 4);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const token = useAuthStore.getState().accessToken;
      const res = await fetch(formsApi.exportCsvUrl(form.id), { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${form.title.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-responses.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e: any) {
      toast({ type: 'error', title: "Couldn't export", message: e?.message });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="card p-0 overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-grey-100 bg-grey-50/50">
        <div>
          <span className="text-sm font-medium text-secondary">Submitted responses</span>
          <span className="text-xs text-grey-400 tabular-nums ml-2">({submitted.length})</span>
        </div>
        <Button variant="outline" size="sm" onClick={exportCsv} disabled={submitted.length === 0} isLoading={exporting}>
          <Download className="w-4 h-4 mr-1.5" /> Export CSV
        </Button>
      </div>

      {submitted.length === 0 ? (
        <p className="px-4 py-10 text-sm text-grey-500 text-center">No submissions yet. {responses.length ? `${responses.length} ${responses.length === 1 ? 'person has' : 'people have'} it in progress.` : ''}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-grey-500 border-b border-grey-100">
                <th className="px-4 py-2 font-medium">Respondent</th>
                <th className="px-4 py-2 font-medium whitespace-nowrap">Submitted</th>
                {inline.map((c) => (
                  <th key={c.id} className="px-4 py-2 font-medium max-w-[200px] truncate" title={c.label}>
                    {c.label}
                  </th>
                ))}
                {columns.length > inline.length && <th className="px-4 py-2 font-medium text-grey-400">+{columns.length - inline.length} more</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-grey-100">
              {submitted.map((r) => (
                <tr key={r.id} onClick={() => onOpenResponse(r.id)} className="hover:bg-grey-50 cursor-pointer">
                  <td className="px-4 py-2.5">
                    <button type="button" className="flex items-center gap-2 text-left" onClick={(e) => { e.stopPropagation(); onOpenResponse(r.id); }}>
                      <Avatar name={r.user?.name || r.user?.email || '?'} src={r.user?.avatarUrl || undefined} size="xs" />
                      <span className="font-medium text-secondary truncate max-w-[180px]">{r.user?.name || r.user?.email}</span>
                    </button>
                  </td>
                  <td className="px-4 py-2.5 text-grey-600 whitespace-nowrap">
                    {formatDateShort(r.submittedAt)}
                    {r.formVersion && r.formVersion !== form.version ? <span className="ml-1 text-xs text-grey-400">v{r.formVersion}</span> : null}
                  </td>
                  {inline.map((c) => (
                    <td key={c.id} className="px-4 py-2.5 text-grey-600 max-w-[200px] truncate">
                      {formatAnswer(c, r.answers?.[c.id], new Map((r.attachments || []).map((a) => [a.id, a.fileName])))}
                    </td>
                  ))}
                  {columns.length > inline.length && <td className="px-4 py-2.5 text-grey-400 text-xs">…</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openResponseId && <ResponseDetail formId={form.id} responseId={openResponseId} onClose={() => onOpenResponse(null)} onChanged={onChanged} />}
    </div>
  );
}

function ResponseDetail({ formId, responseId, onClose, onChanged }: { formId: string; responseId: string; onClose: () => void; onChanged: () => void }) {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['formResponse', formId, responseId],
    queryFn: () => formsApi.getResponse(formId, responseId),
  });

  const reopenMutation = useMutation({
    mutationFn: () => formsApi.reopen(formId, responseId),
    onSuccess: () => {
      toast({ type: 'success', title: 'Response reopened', message: 'They can edit and submit again.' });
      refetch();
      onChanged();
    },
    onError: (e: any) => toast({ type: 'error', title: "Couldn't reopen", message: e?.message }),
  });

  return (
    <Takeover onClose={onClose} maxWidthClass="max-w-3xl">
      <div className="bg-white rounded-xl shadow-xl p-6">
        {isLoading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-6 h-6 text-grey-400 animate-spin" />
          </div>
        ) : isError || !data ? (
          <div>
            <p className="text-sm text-secondary">Couldn't load this response.</p>
            <Button variant="outline" size="sm" className="mt-3" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        ) : (
          <>
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 pb-4 mb-5 border-b border-grey-100 pr-8">
              <div className="flex items-center gap-3">
                <Avatar name={data.user?.name || data.user?.email || '?'} src={data.user?.avatarUrl || undefined} size="md" />
                <div>
                  <p className="font-medium text-secondary">{data.user?.name || data.user?.email}</p>
                  <p className="text-xs text-grey-500">
                    {data.user?.name ? `${data.user.email} · ` : ''}
                    {data.status === 'submitted' ? `Submitted ${formatDateShort(data.submittedAt)}` : 'In progress'}
                    {data.formVersion ? ` · form v${data.formVersion}` : ''}
                  </p>
                </div>
                <ResponseStatusPill status={data.status} className="ml-1" />
              </div>
              {data.status === 'submitted' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (window.confirm('Reopen this response? The respondent will be able to change their answers and will be notified.')) reopenMutation.mutate();
                  }}
                  isLoading={reopenMutation.isPending}
                  className="shrink-0"
                >
                  <RotateCcw className="w-4 h-4 mr-1.5" /> Reopen
                </Button>
              )}
            </div>
            {data.reopenedAt && data.status !== 'submitted' && <p className={cn('text-xs text-warning mb-4')}>Reopened {formatDateShort(data.reopenedAt)} — waiting on the respondent.</p>}
            <FormRenderer
              fields={data.effectiveFields ?? []}
              answers={data.answers || {}}
              readOnly
              upload={{ organizationId: '', responseId, attachments: data.attachments || [], onAttachmentsChange: () => {} }}
            />
          </>
        )}
      </div>
    </Takeover>
  );
}
